import type { LocationQuery, RouteLocationRaw, Router } from 'vue-router'

import { ROUTE_LOGIN } from './names'

/**
 * D-35: after signing in, return to the link the user opened. The session guard sends a signed-out visitor to
 * `/login?redirect=<the page's full path>` (query and hash included); the login page goes there after a successful
 * sign-in (2FA included) when the value is an internal path of a page that needs a session — anything else, and `/`,
 * goes to the home route. The guards check the target as for any navigation (no access → the not-authorized page).
 * The auth pages carry the parameter on their links to each other, so a detour through "Create an account" or "Forgot
 * Password?" keeps it.
 */
export const RETURN_QUERY = 'redirect'

/** A legacy app path is never this long; anything longer is refused rather than parsed. */
const MAX_LENGTH = 2048

function hasControlCharacter(value: string): boolean {
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index)
    if (code < 0x20 || code === 0x7f) return true
  }
  return false
}

/**
 * `value` when it is a path inside this app — one `/` followed by something other than `/` or `\` (`//host` and `/\host`
 * are other origins to a browser), no backslash anywhere, no control character — else `null`. A full URL
 * (`https://…`), a protocol-relative one and anything that is not a single string are refused.
 */
export function internalPath(value: unknown): string | null {
  if (typeof value !== 'string' || !value || value.length > MAX_LENGTH) return null
  if (!value.startsWith('/') || value.startsWith('//') || value.includes('\\')) return null
  if (hasControlCharacter(value)) return null
  return value
}

/** The login location for a signed-out visitor of `to`: its full path rides along as `?redirect=` (not for `/`). */
export function loginLocation(to: { fullPath?: string }): RouteLocationRaw {
  const target = internalPath(to.fullPath)
  return target && target !== '/' ? { name: ROUTE_LOGIN, query: { [RETURN_QUERY]: target } } : { name: ROUTE_LOGIN }
}

/**
 * Where to go after signing in: the `redirect` query when it is an internal path that resolves to a page needing a
 * session (not `/`, not a public page — sign-in, error and public pages are `meta.public`), else `null` and the caller
 * uses the home rule.
 */
export function returnTarget(router: Pick<Router, 'resolve'>, query: LocationQuery): string | null {
  const path = internalPath(query[RETURN_QUERY])
  if (!path || path === '/') return null
  try {
    const resolved = router.resolve(path)
    if (!resolved.matched.length || resolved.meta.public) return null
  } catch {
    return null
  }
  return path
}

/** `{ redirect }` to keep on a link between the auth pages, or `{}` when the current URL carries none (or a bad one). */
export function returnQuery(query: LocationQuery): Record<string, string> {
  const path = internalPath(query[RETURN_QUERY])
  return path ? { [RETURN_QUERY]: path } : {}
}
